import React, { useState, useEffect } from 'react';
import { getAllUsers, updateUser, updateUserCredits, deleteUser, getUserCredits } from '../services/api';

function AdminUserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreditsModal, setShowCreditsModal] = useState(null);
  const [creditsInput, setCreditsInput] = useState('');
  const [creditsOperation, setCreditsOperation] = useState('set');
  const [creditsModalType, setCreditsModalType] = useState('calling'); // 'calling' | 'validation'
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const limit = 20;

  useEffect(() => {
    checkAdminStatus();
    loadUsers();
  }, [page]);

  const checkAdminStatus = async () => {
    try {
      const response = await getUserCredits();
      if (response.success && response.isAdmin) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllUsers(page, limit);
      if (response.success) {
        setUsers(response.data);
        setTotalPages(response.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      if (error.response?.status === 403) {
        setIsAdmin(false);
        setError('Admin access required');
      } else {
        setError(error.response?.data?.message || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.role || 'user',
      calling_credit: user.calling_credit ?? user.credits ?? 10,
      validation_credit: user.validation_credit ?? user.credits ?? 10,
    });
    setError(null);
    setSuccess(null);
  };

  const handleSave = async (userId) => {
    setError(null);
    setSuccess(null);
    try {
      const response = await updateUser(userId, editForm);
      if (response.success) {
        setSuccess('User updated successfully');
        setEditingUser(null);
        loadUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleCancel = () => {
    setEditingUser(null);
    setEditForm({});
    setError(null);
    setSuccess(null);
  };

  const handleOpenCreditsModal = (user, type = 'calling') => {
    setShowCreditsModal(user.id);
    setCreditsModalType(type);
    const val = type === 'validation' 
      ? (user.validation_credit ?? user.credits ?? 10) 
      : (user.calling_credit ?? user.credits ?? 10);
    setCreditsInput(String(val));
    setCreditsOperation('set');
    setError(null);
    setSuccess(null);
  };

  const handleUpdateCredits = async (userId) => {
    setError(null);
    setSuccess(null);
    try {
      const credits = parseInt(creditsInput);
      if (isNaN(credits) || credits < 0) {
        setError('Credits must be a valid non-negative number');
        return;
      }

      const response = await updateUserCredits(userId, credits, creditsOperation, creditsModalType);
      if (response.success) {
        const label = creditsModalType === 'validation' ? 'Validation credits' : 'Calling credits';
        setSuccess(`${label} ${creditsOperation === 'add' ? 'added' : creditsOperation === 'subtract' ? 'subtracted' : 'updated'} successfully`);
        setShowCreditsModal(null);
        setCreditsInput('');
        loadUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update credits');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    setError(null);
    setSuccess(null);
    try {
      const response = await deleteUser(userId);
      if (response.success) {
        setSuccess('User deleted successfully');
        loadUsers();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete user');
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-semibold text-white mb-2">Access Denied</h1>
          <p className="text-gray-400">You need admin privileges to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-white mb-2">👑 Admin - User Management</h1>
          <p className="text-sm text-gray-400">Manage all users, edit details, and update credits</p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-900/20 border border-green-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-sm text-green-400">{success}</p>
          </div>
        )}

        {/* Stats */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 mb-8 backdrop-blur-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400">{users.length}</div>
              <div className="text-sm text-gray-400 mt-1">Current Page</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-400">
                {users.filter(u => u.role === 'admin').length}
              </div>
              <div className="text-sm text-gray-400 mt-1">Admins</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">
                {users.filter(u => u.role === 'user').length}
              </div>
              <div className="text-sm text-gray-400 mt-1">Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-400">
                {users.reduce((sum, u) => sum + (u.calling_credit ?? u.credits ?? 0), 0) + users.reduce((sum, u) => sum + (u.validation_credit ?? u.credits ?? 0), 0)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Total Credits</div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg overflow-hidden backdrop-blur-sm">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-12 w-12 mx-auto rounded-full"></div>
              <p className="mt-4 text-gray-400">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>No users found.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0a0e27] border-b border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Call / Validate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-[#0a0e27]/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{user.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUser === user.id ? (
                            <input
                              type="text"
                              value={editForm.username}
                              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                              className="w-full px-3 py-1 bg-black/50 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                          ) : (
                            <div className="text-sm font-medium text-white">{user.username}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUser === user.id ? (
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              className="w-full px-3 py-1 bg-black/50 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                            />
                          ) : (
                            <div className="text-sm text-white">{user.email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUser === user.id ? (
                            <select
                              value={editForm.role}
                              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                              className="w-full px-3 py-1 bg-black/50 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              user.role === 'admin' ? 'bg-purple-900/30 text-purple-400 border border-purple-500/30' : 'bg-blue-900/30 text-blue-400 border border-blue-500/30'
                            }`}>
                              {user.role || 'user'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUser === user.id ? (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-purple-400 w-14">Call:</span>
                                <input
                                  type="number"
                                  value={editForm.calling_credit ?? 10}
                                  onChange={(e) => setEditForm({ ...editForm, calling_credit: parseInt(e.target.value) || 0 })}
                                  className="w-16 px-2 py-1 bg-black/50 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                                  min="0"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-emerald-400 w-14">Valid:</span>
                                <input
                                  type="number"
                                  value={editForm.validation_credit ?? 10}
                                  onChange={(e) => setEditForm({ ...editForm, validation_credit: parseInt(e.target.value) || 0 })}
                                  className="w-16 px-2 py-1 bg-black/50 border border-gray-700 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                                  min="0"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-0.5 text-xs">
                              <div><span className="text-purple-400">Call:</span> <span className="text-white font-medium">{user.calling_credit ?? user.credits ?? 0}</span></div>
                              <div><span className="text-emerald-400">Valid:</span> <span className="text-white font-medium">{user.validation_credit ?? user.credits ?? 0}</span></div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {editingUser === user.id ? (
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleSave(user.id)}
                                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-all"
                              >
                                ✓ Save
                              </button>
                              <button
                                onClick={handleCancel}
                                className="px-3 py-1 text-xs bg-gray-800 text-white rounded hover:bg-gray-700 transition-all"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEdit(user)}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleOpenCreditsModal(user, 'calling')}
                                className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-all"
                                title="Update calling credits"
                              >
                                Call
                              </button>
                              <button
                                onClick={() => handleOpenCreditsModal(user, 'validation')}
                                className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-all"
                                title="Update validation credits"
                              >
                                Valid
                              </button>
                              <button
                                onClick={() => handleDelete(user.id, user.username)}
                                className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-all"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between bg-[#0a0e27]/50">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-400">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Credits Modal */}
        {showCreditsModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-md shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4">
                Update {creditsModalType === 'validation' ? 'Validation' : 'Calling'} Credits
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Credit Type</label>
                  <select
                    value={creditsModalType}
                    onChange={(e) => {
                      setCreditsModalType(e.target.value);
                      const u = users.find(x => x.id === showCreditsModal);
                      if (u) {
                        const val = e.target.value === 'validation' 
                          ? (u.validation_credit ?? u.credits ?? 10) 
                          : (u.calling_credit ?? u.credits ?? 10);
                        setCreditsInput(String(val));
                      }
                    }}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="calling">Calling Credits (for calls)</option>
                    <option value="validation">Validation Credits (for record validation)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Operation</label>
                  <select
                    value={creditsOperation}
                    onChange={(e) => setCreditsOperation(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="set">Set to</option>
                    <option value="add">Add</option>
                    <option value="subtract">Subtract</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Amount</label>
                  <input
                    type="number"
                    min="0"
                    value={creditsInput}
                    onChange={(e) => setCreditsInput(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => {
                    setShowCreditsModal(null);
                    setCreditsInput('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateCredits(showCreditsModal)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUserManagement;
